import { useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { api, getSession, onSessionChange, request, setSession } from "@/lib/api";
import type { AdminUser } from "@/lib/types";

interface AuthState {
  user: AdminUser | null;
  ready: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  can: (permission: string) => boolean;
  reload: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: AdminUser;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [ready, setReady] = useState(false);
  const queryClient = useQueryClient();

  const reload = useCallback(async () => {
    if (!getSession()) {
      setUser(null);
      return;
    }
    try {
      setUser(await api.get<AdminUser>("/admin/v1/auth/me"));
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    reload().finally(() => setReady(true));
    return onSessionChange((s) => {
      if (!s) {
        setUser(null);
        queryClient.clear();
      }
    });
  }, [reload, queryClient]);

  const login = useCallback(async (email: string, password: string) => {
    const data = await request<TokenResponse>("/admin/v1/auth/login", { method: "POST", body: { email, password }, auth: false });
    setSession({ accessToken: data.access_token, refreshToken: data.refresh_token, expiresAt: Date.now() + data.expires_in * 1000 });
    setUser(data.user);
  }, []);

  const logout = useCallback(async () => {
    const s = getSession();
    if (s) {
      try {
        await request("/admin/v1/auth/logout", { method: "POST", body: { refresh_token: s.refreshToken }, auth: false });
      } catch {
        /* best effort */
      }
    }
    setSession(null);
  }, []);

  const can = useCallback(
    (permission: string) => {
      const perms = user?.permissions ?? [];
      return perms.includes("*") || perms.includes(permission) || perms.includes(`${permission.split(".")[0]}.*`);
    },
    [user],
  );

  const value = useMemo(() => ({ user, ready, login, logout, can, reload }), [user, ready, login, logout, can, reload]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
